/**
 * @component ry-hover-card
 * @category  overlay
 * @summary   Rich hover preview anchored to a trigger element. Like a tooltip but with arbitrary HTML content — used for user profile previews, link previews, inline definitions. Opens on hover/focus with a configurable delay, closes when the cursor leaves both trigger and card. Composes from the Floating UI engine for placement / flip / shift.
 *
 * Reference: similar to Radix UI's HoverCard. Distinct from `<ry-tooltip>` in
 * that the card body can be hovered (it stays open while the cursor is over
 * the card, allowing links inside to be clicked). Tooltip is text-only and
 * closes the moment the cursor leaves the trigger.
 *
 * @attribute for          CSS selector?   Required. Trigger element(s) — same pattern as ry-context-menu.
 * @attribute placement    Placement       default="bottom-start"   Initial placement; auto-flips.
 * @attribute offset       number          default=8                Pixels of gap between trigger and card.
 * @attribute open-delay   number          default=500              Milliseconds to wait after mouseenter/focus before showing.
 * @attribute close-delay  number          default=200              Milliseconds to wait after mouseleave/blur before hiding. Provides forgiveness for the cursor traveling from trigger to card.
 *
 * @property  state        'open' | 'closed'
 *
 * @method    open()       Programmatically show (bypasses open-delay).
 * @method    close()      Programmatically hide (bypasses close-delay).
 *
 * @event ry:beforeopen    detail: {}   fires at the top of open() — after the hover/focus delay elapses, or el.open() — before any state change   cancelable: preventDefault() keeps the card closed; ry:open does not fire
 * @event ry:open    detail: {}   fires after the card opens
 * @event ry:beforeclose   detail: {}   fires at the top of close() — after the leave/blur delay, Escape, or el.close() — before any state change   cancelable: preventDefault() keeps the card open; ry:close does not fire
 * @event ry:close   detail: {}   fires after the card closes

 *
 * @keyboard  Escape — closes the card when open.
 *
 * @state     `data-ry-state` of `"open"` / `"closed"` on host.
 *
 * @content   Free-form rich content. Authored children become the card body.
 *
 * @dom
 *   ry-hover-card[for][data-ry-state]
 *     > div.ry-hover-card__panel  [data-ry-target="panel"]   (portals to <body> on open)
 *       > {authored content}
 *
 * @gotcha  `[open-delay]` and `[close-delay]` work together: the card stays open as the cursor travels from trigger to card if (close-delay) > (transit time). Default 200ms is generous.
 *
 * @gotcha  Hover doesn't apply to touch devices. On touch, the card opens on focus (tap-to-focus) but never times out — it stays open until tap-outside or Escape. Document explicitly if your target audience is mobile-first.
 *
 * @gotcha  The `[for]` attribute is REQUIRED. Without a trigger, there's nothing to hover. Unlike ry-context-menu, hover-card does NOT default to `parentElement` because the most common pattern is to define multiple triggers via selector (`[for=".user-link"]`).
 *
 * @example basic
 * ```html
 * <a class="user-link" href="/u/anna">@anna</a>
 * <ry-hover-card for=".user-link">
 *   <strong>Anna Smith</strong>
 *   <p>Frontend engineer at Acme.</p>
 *   <ry-button size="sm">Follow</ry-button>
 * </ry-hover-card>
 * ```
 *
 * @example top-placement-fast-open
 * ```html
 * <span class="hint">help?</span>
 * <ry-hover-card for=".hint" placement="top" open-delay="100">
 *   This action cannot be undone.
 * </ry-hover-card>
 * ```
 *
 * @tutorial mount-creates-panel
 * ```js
 * const root = mount(`
 *   <div>
 *     <a class="t" href="#">trigger</a>
 *     <ry-hover-card for=".t">content</ry-hover-card>
 *   </div>
 * `);
 * const card = root.querySelector('ry-hover-card');
 * await expectEqual(!!card.querySelector('[data-ry-target="panel"]'), true);
 * ```
 *
 * @tutorial open-method-bypasses-delay
 * ```js
 * const root = mount(`<div><a class="t" href="#">x</a><ry-hover-card for=".t">x</ry-hover-card></div>`);
 * const card = root.querySelector('ry-hover-card');
 * card.open();
 * await expectEqual(card.state, 'open');
 * card.close();
 * await expectEqual(card.state, 'closed');
 * ```
 *
 * @tutorial escape-closes
 * ```js
 * const root = mount(`<div><a class="t" href="#">x</a><ry-hover-card for=".t">x</ry-hover-card></div>`);
 * const card = root.querySelector('ry-hover-card');
 * card.open();
 * document.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape' }));
 * await expectEqual(card.state, 'closed');
 * ```
 *
 * @tutorial beforeopen-veto
 * ```js
 * // preventDefault() on ry:beforeopen keeps the card closed; ry:open is silent.
 * const root = mount(`<div><a class="t" href="#">x</a><ry-hover-card for=".t">x</ry-hover-card></div>`);
 * const card = root.querySelector('ry-hover-card');
 * const opened = [];
 * card.addEventListener('ry:open', () => opened.push(1));
 * card.addEventListener('ry:beforeopen', (e) => e.preventDefault(), { once: true });
 * card.open();
 * await expectEqual(card.state, 'closed', 'vetoed open leaves state closed');
 * await expectEqual(opened.length, 0, 'ry:open did not fire');
 * card.open();
 * await expectEqual(card.state, 'open', 'without the veto, open() works');
 * ```
 *
 * @tutorial beforeclose-veto
 * ```js
 * // preventDefault() on ry:beforeclose keeps the card open (Escape or el.close()); ry:close is silent.
 * const root = mount(`<div><a class="t" href="#">x</a><ry-hover-card for=".t">x</ry-hover-card></div>`);
 * const card = root.querySelector('ry-hover-card');
 * card.open();
 * const closed = [];
 * card.addEventListener('ry:close', () => closed.push(1));
 * card.addEventListener('ry:beforeclose', (e) => e.preventDefault(), { once: true });
 * document.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape' }));
 * await expectEqual(card.state, 'open', 'vetoed close leaves state open');
 * await expectEqual(closed.length, 0, 'ry:close did not fire');
 * card.close();
 * await expectEqual(card.state, 'closed', 'without the veto, close() works');
 * ```
 *
 * @see ry-tooltip
 * @see ry-popover
 */


import { RyElement } from '../core/ry-element.js';
import { attachFloating, type Placement } from '../core/ry-floating.js';

export class RyHoverCard extends RyElement {
  #panel: HTMLElement | null = null;
  #floatingCleanup: (() => void) | null = null;
  #portalRestore: (() => void) | null = null;
  #openTimer: number | null = null;
  #closeTimer: number | null = null;
  #activeTrigger: HTMLElement | null = null;

  setup(): void {
    // Wrap children in panel
    const authored = Array.from(this.childNodes);
    const panel = document.createElement('div');
    panel.setAttribute('data-ry-target', 'panel');
    panel.className = 'ry-hover-card__panel';
    panel.style.position = 'absolute';
    panel.style.top = '0';
    panel.style.left = '0';
    for (const node of authored) panel.appendChild(node);

    this.innerHTML = '';
    this.appendChild(panel);
    this.#panel = panel;

    if (!this.hasAttribute('data-ry-state')) this.state = 'closed';
    if (!this.hasAttribute('role')) this.setAttribute('role', 'tooltip');

    // Hover/focus on triggers
    const selector = this.getAttribute('for');
    const targets: Element[] = selector
      ? Array.from(document.querySelectorAll(selector))
      : [];

    targets.forEach(trigger => {
      const t = trigger as HTMLElement;
      this.on(t, 'mouseenter', () => this.#scheduleOpen(t));
      this.on(t, 'mouseleave', () => this.#scheduleClose());
      this.on(t, 'focusin', () => this.#scheduleOpen(t));
      this.on(t, 'focusout', () => this.#scheduleClose());
    });

    // Card itself: cancel close on hover (so the cursor can travel from
    // trigger to card without the card disappearing)
    this.on(panel, 'mouseenter', () => this.#cancelClose());
    this.on(panel, 'mouseleave', () => this.#scheduleClose());

    // Escape closes
    this.onEscape(() => this.close(), { skip: () => this.state !== 'open' });
  }

  open(): void {
    this.#cancelOpen();
    this.#cancelClose();
    if (this.state === 'open') return;
    if (!this.#panel || !this.#activeTrigger) {
      // open() called with no trigger — anchor to the first trigger we know about
      const selector = this.getAttribute('for');
      if (selector) {
        this.#activeTrigger = document.querySelector(selector);
      }
    }
    if (!this.#panel || !this.#activeTrigger) return;
    if (!this.emit('beforeopen')) return;

    // Mirror state onto the panel — CSS keys on its OWN [data-ry-state] so

    // visibility/transition work after portal.
    this.#panel.setAttribute('data-ry-state', 'open');
    this.#portalRestore = this.portalToBody(this.#panel);

    const placement = (this.getAttribute('placement') ?? 'bottom-start') as Placement;
    const offset = Number(this.getAttribute('offset') ?? 8);
    this.#floatingCleanup = attachFloating(this.#activeTrigger, this.#panel, {
      placement,
      offset,
      flip: true,
      shift: true,
      reflectPlacementAttribute: true,
    });

    this.state = 'open';
    this.emit('open');
  }

  close(): void {
    this.#cancelOpen();
    this.#cancelClose();
    if (this.state === 'closed') return;
    if (!this.emit('beforeclose')) return;

    this.state = 'closed';
    if (this.#panel) this.#panel.setAttribute('data-ry-state', 'closed');

    this.#floatingCleanup?.();
    this.#floatingCleanup = null;
    this.#portalRestore?.();
    this.#portalRestore = null;
    this.emit('close');
  }

  #scheduleOpen(trigger: HTMLElement): void {
    this.#cancelClose();
    this.#activeTrigger = trigger;
    const delay = Number(this.getAttribute('open-delay') ?? 500);
    if (delay <= 0) {
      this.open();
      return;
    }
    this.#cancelOpen();
    this.#openTimer = window.setTimeout(() => {
      this.#openTimer = null;
      this.open();
    }, delay);
  }

  #scheduleClose(): void {
    this.#cancelOpen();
    const delay = Number(this.getAttribute('close-delay') ?? 200);
    if (delay <= 0) {
      this.close();
      return;
    }
    this.#cancelClose();
    this.#closeTimer = window.setTimeout(() => {
      this.#closeTimer = null;
      this.close();
    }, delay);
  }

  #cancelOpen(): void {
    if (this.#openTimer != null) {
      clearTimeout(this.#openTimer);
      this.#openTimer = null;
    }
  }
  #cancelClose(): void {
    if (this.#closeTimer != null) {
      clearTimeout(this.#closeTimer);
      this.#closeTimer = null;
    }
  }

  teardown(): void {
    this.#cancelOpen();
    this.#cancelClose();
    this.#floatingCleanup?.();
    this.#portalRestore?.();
  }
}

customElements.define('ry-hover-card', RyHoverCard);
